import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { getRequestAuthHeaders } from "@/lib/requestAuth";
import { useProfile } from "@/hooks/use-profile";
import {  ArrowLeft, Sparkles, Maximize, Minimize, RefreshCw, Eye, EyeOff, ListOrdered, PenTool, Eraser, Undo, Trash2, Palette, MousePointerClick, Search } from "lucide-react";
import MathDisplay from "@/components/MathDisplay";
import MathText from "@/components/MathText";
import ModuleLockedState from "@/components/ModuleLockedState";
import { DrawingCanvas, type DrawingCanvasRef, type EraserType } from "@/components/DrawingCanvas";
import { useModuleStatus } from "@/hooks/use-module-status";
import { useRolePerspective } from "@/hooks/use-role-perspective";
import type { Topic, DifficultyLevel } from "@shared/schema";
import { selectBalancedItems } from "@/lib/questionSelection";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useSubject } from "@/contexts/SubjectContext";
import { getYearCategory, CATEGORY_ORDER, isSubjectMatch, formatYearGroup } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface GeneratedQuestion {
    topicName: string;
    question: string;
    answer: string;
    explanation: string;
    difficulty: DifficultyLevel;
}

const diffLabel = (d: number) => `Grade ${d}`;

const getQuestionAnswer = (question: Topic["questions"][number]) =>
    question.answer || (question.options && question.options.length > 0 ? question.options[question.correctAnswer] : "");

export default function TeacherStarter() {
    const [, setLocation] = useLocation();
    const { profile } = useProfile();
    const { effectiveRole } = useRolePerspective(profile?.id, profile?.role);
    const { currentSubject } = useSubject();
    // Topics loaded via React Query for caching (staleTime: Infinity by default)
    const { data: rawTopics = [], isLoading: loading } = useQuery<Topic[]>({
        queryKey: ["topics", "starter", currentSubject?.id],
        queryFn: async () => {
            if (!currentSubject) return [];
            const authHeaders = await getRequestAuthHeaders();
            const response = await fetch(`/api/topics?mode=starter&subject=${currentSubject.id}`, { headers: authHeaders });
            const data = response.ok ? await response.json() : [];
            return data.filter((t: Topic) => t.mode === 'starter' && isSubjectMatch(t, currentSubject.label));
        },
        enabled: !!currentSubject,
    });
    const topics = rawTopics;
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
    const [starterCount, setStarterCount] = useState<number>(4);
    const [generatedQuestions, setGeneratedQuestions] = useState<GeneratedQuestion[]>([]);
    const [isGenerated, setIsGenerated] = useState(false);
    const [revealedAnswers, setRevealedAnswers] = useState<Set<number>>(new Set());
    const [cardDifficulties, setCardDifficulties] = useState<Map<number, DifficultyLevel>>(new Map());
    const [cardTopics, setCardTopics] = useState<Map<number, string>>(new Map());
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [showOnlyMyTopics, setShowOnlyMyTopics] = useState(false);
    const [fullscreenCardIdx, setFullscreenCardIdx] = useState<number | null>(null);
    const [closingFullscreenCardIdx, setClosingFullscreenCardIdx] = useState<number | null>(null);
    const [fullscreenCardRect, setFullscreenCardRect] = useState<DOMRect | null>(null);
    const [isFullscreenCardExpanded, setIsFullscreenCardExpanded] = useState(false);
    const singleFullscreenCloseTimerRef = useRef<number | null>(null);
    const singleFullscreenOpenFrameRef = useRef<number | null>(null);
    const singleCardRefs = useRef<Array<HTMLDivElement | null>>([]);
    // Auto text sizing: base computed from viewport, manual offset from A+/A- buttons
    const [manualTextOffset, setManualTextOffset] = useState(() => {
        try {
            const saved = localStorage.getItem("starterTextSizeOffset");
            return saved ? parseFloat(saved) : 0;
        } catch { return 0; }
    });
    const [autoTextBase, setAutoTextBase] = useState(1);
    const textSizeMultiplier = Math.max(0.4, Math.min(2.5, autoTextBase + manualTextOffset));
    const setTextSizeMultiplier = useCallback((fn: (prev: number) => number) => {
        setManualTextOffset(prev => {
            const currentTotal = Math.max(0.4, Math.min(2.5, autoTextBase + prev));
            const newTotal = fn(currentTotal);
            return newTotal - autoTextBase;
        });
    }, [autoTextBase]);

    // Drawing Tool State
    const [isDrawingMode, setIsDrawingMode] = useState(false);
    const [isSingleCardDrawingMode, setIsSingleCardDrawingMode] = useState(false);
    const mainCanvasRef = useRef<DrawingCanvasRef>(null);
    const fsCanvasRef = useRef<DrawingCanvasRef>(null);
    const singleCanvasRef = useRef<DrawingCanvasRef>(null);
    const [brushSize, setBrushSize] = useState([3]);
    const [singleBrushSize, setSingleBrushSize] = useState([5]);
    const [mainEraserType, setMainEraserType] = useState<EraserType>('brush');
    const [singleEraserType, setSingleEraserType] = useState<EraserType>('brush');
    const colors = ["#ef4444", "#3b82f6", "#22c55e", "#000000", "#ffffff"];

    // Get the active canvas ref based on current mode
    const getActiveMainCanvasRef = () => {
        return isFullscreen ? fsCanvasRef : mainCanvasRef;
    };

    const containerRef = useRef<HTMLDivElement>(null);
    const { status: moduleStatus, loading: moduleStatusLoading } = useModuleStatus('starter');

    // Persist manual offset
    useEffect(() => {
        try {
            localStorage.setItem("starterTextSizeOffset", manualTextOffset.toString());
        } catch {}
    }, [manualTextOffset]);

    // Auto-calculate base text scale from viewport dimensions
    useEffect(() => {
        const computeAutoBase = () => {
            const w = window.innerWidth;
            const h = window.innerHeight;
            // Reference: 1920x1080 = scale 1.0
            const wScale = w / 1920;
            const hScale = h / 1080;
            // Use the smaller dimension to ensure text fits
            const base = Math.min(wScale, hScale);
            // Clamp between 0.5 and 1.6
            setAutoTextBase(Math.max(0.5, Math.min(1.6, base)));
        };
        computeAutoBase();
        window.addEventListener('resize', computeAutoBase);
        return () => window.removeEventListener('resize', computeAutoBase);
    }, []);

    useEffect(() => {
        return () => {
            if (singleFullscreenOpenFrameRef.current !== null) {
                window.clearTimeout(singleFullscreenOpenFrameRef.current);
            }
            if (singleFullscreenCloseTimerRef.current !== null) {
                window.clearTimeout(singleFullscreenCloseTimerRef.current);
            }
        };
    }, []);

    // ── Fullscreen API ───────────────────────────────────────────────────────
    useEffect(() => {
        const handler = () => {
            const nowFs = !!document.fullscreenElement;
            setIsFullscreen(nowFs);
            // Clear drawing canvases on fullscreen transition to avoid offset issues
            if (nowFs) {
                mainCanvasRef.current?.clear();
            } else {
                fsCanvasRef.current?.clear();
            }
        };
        document.addEventListener("fullscreenchange", handler);
        document.addEventListener("webkitfullscreenchange", handler);
        return () => {
            document.removeEventListener("fullscreenchange", handler);
            document.removeEventListener("webkitfullscreenchange", handler);
        };
    }, []);

    const enterFullscreen = async () => {
        const el = containerRef.current || document.documentElement;
        try {
            if (el.requestFullscreen) await el.requestFullscreen();
            else if ((el as any).webkitRequestFullscreen) (el as any).webkitRequestFullscreen();
        } catch (e) { console.error(e); }
    };

    const exitFullscreen = async () => {
        try {
            if (document.exitFullscreen) await document.exitFullscreen();
            else if ((document as any).webkitExitFullscreen) (document as any).webkitExitFullscreen();
        } catch (e) { console.error(e); }
    };

    // Topics are loaded via useQuery above (cached, no manual fetch needed)

    const toggleTopic = (id: string) => {
        setSelectedTopics(prev =>
            prev.includes(id)
                ? prev.filter(x => x !== id)
                : prev.length < starterCount
                    ? [...prev, id]
                    : prev
        );
    };

    // ── Generate ─────────────────────────────────────────────────────────────
    const handleGenerate = () => {
        if (selectedTopics.length === 0) return;

        clearSingleFullscreenCloseTimer();
        clearSingleFullscreenOpenFrame();
        setClosingFullscreenCardIdx(null);
        setIsFullscreenCardExpanded(false);
        setFullscreenCardRect(null);
        setFullscreenCardIdx(null);
        const topicPools = selectedTopics
            .map((topicId) => {
                const topic = topics.find((candidate) => candidate.id === topicId);
                if (!topic) return [];

                return topic.questions.map((question) => ({
                    topicName: topic.name,
                    question: question.question,
                    answer: getQuestionAnswer(question),
                    explanation: question.explanation,
                    difficulty: question.difficulty,
                }));
            })
            .filter((pool) => pool.length > 0);

        const selected = selectBalancedItems(topicPools, starterCount);

        // Sort by difficulty ascending (grade 4 → grade 9)
        selected.sort((a, b) => a.difficulty - b.difficulty);

        setGeneratedQuestions(selected);
        setIsGenerated(true);
        setRevealedAnswers(new Set());
        const d = new Map<number, DifficultyLevel>();
        const t2 = new Map<number, string>();
        selected.forEach((q, i) => { d.set(i, q.difficulty); t2.set(i, q.topicName); });
        setCardDifficulties(d);
        setCardTopics(t2);
    };

    // ── Refresh single card ──────────────────────────────────────────────────
    const refreshCard = (idx: number, diffOverride?: DifficultyLevel) => {
        const diff = diffOverride ?? cardDifficulties.get(idx) ?? 3;
        const topicName = cardTopics.get(idx) ?? generatedQuestions[idx]?.topicName;
        const topic = topics.find(t => t.name === topicName);
        if (!topic) return;

        let pool = topic.questions.filter(q => q.difficulty === diff);
        if (!pool.length) {
            let minD = Infinity;
            topic.questions.forEach(q => { const d = Math.abs(q.difficulty - diff); if (d < minD) minD = d; });
            pool = topic.questions.filter(q => Math.abs(q.difficulty - diff) === minD);
        }
        if (!pool.length) pool = topic.questions;

        const current = generatedQuestions[idx];
          const availablePool = pool.filter(q => q.question !== current?.question);
          const q = availablePool.length > 0 
              ? availablePool[Math.floor(Math.random() * availablePool.length)]
              : topic.questions[Math.floor(Math.random() * topic.questions.length)];
        setGeneratedQuestions(prev => { const u = [...prev]; u[idx] = { topicName: topic.name, question: q.question, answer: getQuestionAnswer(q), explanation: q.explanation, difficulty: q.difficulty }; return u; });
        setRevealedAnswers(prev => { const s = new Set(prev); s.delete(idx); return s; });
    };

    const changeCardTopic = (idx: number, topicName: string) => {
        const topic = topics.find(t => t.name === topicName);
        if (!topic) return;
        const diff = cardDifficulties.get(idx) ?? 3;
        let pool = topic.questions.filter(q => q.difficulty === diff);
        if (!pool.length) pool = topic.questions;
        if (!pool.length) return;
        const q = pool[Math.floor(Math.random() * pool.length)];
        setGeneratedQuestions(prev => { const u = [...prev]; u[idx] = { topicName: topic.name, question: q.question, answer: getQuestionAnswer(q), explanation: q.explanation, difficulty: q.difficulty }; return u; });
        setCardTopics(prev => { const m = new Map(prev); m.set(idx, topicName); return m; });
        setRevealedAnswers(prev => { const s = new Set(prev); s.delete(idx); return s; });
    };

    const updateDifficulty = (idx: number, val: DifficultyLevel) => {
        setCardDifficulties(prev => { const m = new Map(prev); m.set(idx, val); return m; });
        refreshCard(idx, val);
    };

    const toggleReveal = (i: number) => {
        setRevealedAnswers(prev => {
            const s = new Set(prev);
            s.has(i) ? s.delete(i) : s.add(i);
            return s;
        });
    };

    const clearSingleFullscreenCloseTimer = () => {
        if (singleFullscreenCloseTimerRef.current !== null) {
            window.clearTimeout(singleFullscreenCloseTimerRef.current);
            singleFullscreenCloseTimerRef.current = null;
        }
    };

    const clearSingleFullscreenOpenFrame = () => {
        if (singleFullscreenOpenFrameRef.current !== null) {
            cancelAnimationFrame(singleFullscreenOpenFrameRef.current);
            singleFullscreenOpenFrameRef.current = null;
        }
    };

    const openSingleFullscreen = (idx: number) => {
        clearSingleFullscreenCloseTimer();
        clearSingleFullscreenOpenFrame();
        setClosingFullscreenCardIdx(null);
        setIsSingleCardDrawingMode(false);
        const cardElement = singleCardRefs.current[idx];
        if (!cardElement) {
            setFullscreenCardRect(null);
            return;
        }

        setFullscreenCardRect(cardElement.getBoundingClientRect());
        setFullscreenCardIdx(idx);
        setIsFullscreenCardExpanded(false);
        singleFullscreenOpenFrameRef.current = requestAnimationFrame(() => {
            singleFullscreenOpenFrameRef.current = requestAnimationFrame(() => {
                singleFullscreenOpenFrameRef.current = null;
                setIsFullscreenCardExpanded(true);
            });
        });
    };

    const closeSingleFullscreen = () => {
        if (fullscreenCardIdx === null) return;

        const idx = fullscreenCardIdx;
        clearSingleFullscreenCloseTimer();
        clearSingleFullscreenOpenFrame();
        setIsSingleCardDrawingMode(false);
        setClosingFullscreenCardIdx(idx);
        setIsFullscreenCardExpanded(false);
        singleFullscreenCloseTimerRef.current = window.setTimeout(() => {
            setFullscreenCardIdx(null);
            setClosingFullscreenCardIdx(null);
            setFullscreenCardRect(null);
            singleFullscreenCloseTimerRef.current = null;
        }, 420);
    };

    const toggleSingleFullscreen = (idx: number) => {
        if (fullscreenCardIdx === idx && closingFullscreenCardIdx !== idx) {
            closeSingleFullscreen();
            return;
        }

        openSingleFullscreen(idx);
    };

    const isTeacher = effectiveRole === 'teacher';
    const baseVisibleTopics = isTeacher && showOnlyMyTopics ? topics.filter(t => !t.is_core) : topics;
    
    // Filter by search query
    const visibleTopics = baseVisibleTopics.filter(t => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return t.name.toLowerCase().includes(query) || 
               (t.category && t.category.toLowerCase().includes(query));
    });

    // Don't render until subject is loaded
    if (!currentSubject) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!moduleStatusLoading && moduleStatus && moduleStatus !== 'active' && effectiveRole !== 'admin') {
        return <ModuleLockedState status={moduleStatus} />;
    }

    // ── GENERATED VIEW ───────────────────────────────────────────────────────
    if (isGenerated) {
        return (
            <div
                ref={containerRef}
                className={`bg-background text-content-wrapper ${isFullscreen ? "starter-fs" : "flex flex-col min-h-[calc(100vh-80px)]"}`}
                style={{ '--text-scale-factor': textSizeMultiplier } as React.CSSProperties}
            >
                <style>{`
                    /* ── Cambria Math / serif font for KaTeX ── */
                    .math-serif .katex,
                    .math-serif .katex * {
                        font-family: 'Cambria Math', 'STIX Two Math', 'Latin Modern Math', 'Computer Modern', Georgia, 'Times New Roman', serif !important;
                    }
                    /* Make KaTeX scale with parent font-size */
                    .math-serif .katex { font-size: 1em; }

                    /* ── Fullscreen layout ── */
                    .starter-fs {
                        width: 100vw; height: 100vh;
                        display: flex; flex-direction: column; overflow: hidden;
                    }
                    .starter-fs .fs-header {
                        flex-shrink: 0;
                        position: relative; z-index: 56;
                        display: flex; align-items: center; gap: 10px;
                        background: hsl(var(--background) / 0.97);
                        backdrop-filter: blur(6px);
                        padding: 8px 16px;
                        border-bottom: 1px solid hsl(var(--border));
                        transition: opacity 0.3s, transform 0.3s;
                    }
                    .starter-fs .fs-grid {
                        flex: 1;
                        display: grid;
                        grid-template-columns: ${generatedQuestions.length === 8 ? "1fr 1fr 1fr 1fr" : generatedQuestions.length === 6 ? "1fr 1fr 1fr" : "1fr 1fr"};
                        grid-template-rows: 1fr 1fr;
                        gap: clamp(8px, 1.5vw, 20px);
                        padding: clamp(8px, 1.5vw, 20px);
                        min-height: 0;
                    }
                    /* ── Card flip ── */
                    .flip-scene {
                        perspective: 1200px;
                        width: 100%; height: 100%;
                    }
                    .flip-inner {
                        position: relative; width: 100%; height: 100%;
                        transform-style: preserve-3d;
                        transition: transform 0.65s cubic-bezier(0.4, 0, 0.2, 1);
                    }
                    .flip-inner.flipped { transform: rotateY(180deg); }
                    .flip-face {
                        position: absolute; inset: 0;
                        backface-visibility: hidden;
                        -webkit-backface-visibility: hidden;
                        pointer-events: auto;
                        border-radius: 16px;
                        overflow: hidden;
                        border: 2px solid hsl(var(--border));
                        background: hsl(var(--card));
                        display: flex; flex-direction: column;
                    }
                    .flip-face.front {
                        transform: rotateY(0deg) translateZ(1px);
                    }
                    .flip-face.back {
                        transform: rotateY(180deg) translateZ(1px);
                        pointer-events: none;
                        border-color: hsl(142 71% 45% / 0.6);
                        background: hsl(142 71% 97%);
                    }
                    .flip-inner.flipped .flip-face.front {
                        pointer-events: none;
                    }
                    .flip-inner.flipped .flip-face.back {
                        pointer-events: auto;
                    }
                    .flip-inner:not(.flipped) .flip-face.back {
                        pointer-events: none;
                    }
                    .dark .flip-face.back {
                        background: hsl(142 30% 12%);
                        border-color: hsl(142 50% 40% / 0.5);
                    }
                    /* Card face header */
                    .flip-face .card-header {
                        display: flex; align-items: center; justify-content: space-between;
                        flex-shrink: 0;
                        padding: clamp(6px, 1vw, 12px) clamp(10px, 1.5vw, 18px);
                        border-bottom: 1px solid hsl(var(--border) / 0.5);
                        background: hsl(var(--muted) / 0.3);
                        gap: 8px;
                    }
                    .single-fs .card-header {
                        position: relative; z-index: 51;
                        pointer-events: auto;
                    }
                    .flip-face.back .card-header {
                        border-bottom-color: hsl(142 71% 45% / 0.2);
                        background: hsl(142 71% 45% / 0.08);
                    }
                    /* Card body */
                    .flip-face .card-body {
                        flex: 1; display: flex; flex-direction: column;
                        align-items: center; justify-content: center;
                        padding: clamp(12px, 2vw, 28px);
                        text-align: center; min-height: 0;
                        gap: 8px;
                        overflow-y: auto;
                    }
                    /* Question text size – scales with viewport */
                    .flip-face .q-text {
                          font-size: ${generatedQuestions.length >= 8 ? "calc(clamp(0.7rem, 1.6vw, 2rem) * var(--text-scale-factor))" : "calc(clamp(1.4rem, 3.5vw, 4.5rem) * var(--text-scale-factor))"};
                          line-height: 1.4;
                          font-weight: 700;
                          color: hsl(var(--foreground));
                          transition: font-size 420ms cubic-bezier(0.22, 1, 0.36, 1);
                      }
                      .flip-face.back .q-text {
                          font-size: ${generatedQuestions.length >= 8 ? "calc(clamp(0.9rem, 1.8vw, 2.2rem) * var(--text-scale-factor))" : "calc(clamp(1.1rem, 2.2vw, 2.8rem) * var(--text-scale-factor))"};
                        color: hsl(var(--muted-foreground));
                        font-weight: 500;
                        opacity: 0.75;
                        transition: font-size 420ms cubic-bezier(0.22, 1, 0.36, 1);
                    }
                    .flip-face .a-text {
                        font-size: calc(clamp(1.8rem, 4.5vw, 6rem) * var(--text-scale-factor));
                        line-height: 1.3;
                        font-weight: 800;
                        color: hsl(142 60% 35%);
                        transition: font-size 420ms cubic-bezier(0.22, 1, 0.36, 1);
                    }
                    .dark .flip-face .a-text { color: hsl(142 60% 65%); }
                    /* Number badge */
                    .q-badge {
                        display: inline-flex; align-items: center; justify-content: center;
                        border-radius: 50%; font-weight: 900; flex-shrink: 0;
                        width: clamp(28px, 3vw, 44px); height: clamp(28px, 3vw, 44px);
                        font-size: clamp(0.75rem, 1.5vw, 1.25rem);
                        background: hsl(var(--primary)); color: hsl(var(--primary-foreground));
                    }
                    /* ctrl buttons in header */
                    .ctrl { font-size: clamp(0.6rem, 0.9vw, 0.85rem) !important; padding: 0.25em 0.6em !important; height: auto !important; }

                    /* Windowed card sizes */
                    .windowed-grid {
                        flex: 1;
                        display: grid;
                        grid-template-columns: ${generatedQuestions.length === 8 ? "1fr 1fr 1fr 1fr" : generatedQuestions.length === 6 ? "1fr 1fr 1fr" : "1fr 1fr"};
                        grid-template-rows: 1fr 1fr;
                        gap: 20px;
                        min-height: 0;
                    }
                    .windowed-flip-scene {
                        perspective: 1400px;
                        height: 100%;
                        width: 100%;
                    }
                    .windowed-flip-scene .flip-inner { height: 100%; width: 100%; }
                    /* Windowed text sizes (smaller than fullscreen) */
                    .windowed-flip-scene .flip-face .q-text, .single-fs-closing .flip-face .q-text { font-size: calc(clamp(1.1rem, 2vw, 1.8rem) * var(--text-scale-factor)); }
                    .windowed-flip-scene .flip-face.back .q-text, .single-fs-closing .flip-face.back .q-text { font-size: calc(clamp(0.9rem, 1.4vw, 1.2rem) * var(--text-scale-factor)); }
                    .windowed-flip-scene .flip-face .a-text, .single-fs-closing .flip-face .a-text { font-size: calc(clamp(1.4rem, 2.5vw, 2.5rem) * var(--text-scale-factor)); }
                    /* ── Windowed individual card fullscreen ── */
                    .single-fs {
                        position: fixed !important;
                        z-index: 9999 !important;
                        padding: 0 !important;
                        background: hsl(var(--background)/0.95);
                        backdrop-filter: blur(10px);
                        transform-origin: top left;
                        transition: top 420ms cubic-bezier(0.22, 1, 0.36, 1), left 420ms cubic-bezier(0.22, 1, 0.36, 1), width 420ms cubic-bezier(0.22, 1, 0.36, 1), height 420ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 420ms cubic-bezier(0.22, 1, 0.36, 1), border-radius 420ms cubic-bezier(0.22, 1, 0.36, 1), background-color 420ms cubic-bezier(0.22, 1, 0.36, 1);
                        will-change: top, left, width, height, opacity, box-shadow, border-radius;
                    }
                    .single-fs-host {
                        perspective: none !important;
                        overflow: visible;
                        z-index: 9998;
                    }
                    .single-fs .flip-face {
                        transition: border-radius 420ms cubic-bezier(0.22, 1, 0.36, 1), border-color 420ms cubic-bezier(0.22, 1, 0.36, 1), background-color 420ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 420ms cubic-bezier(0.22, 1, 0.36, 1);
                    }
                    .single-drawing-active .flip-face {
                        pointer-events: none !important;
                    }
                    .single-fs-closing {
                        pointer-events: none;
                    }
                    .single-fs .flip-inner { height: 100% !important; width: 100% !important; }
                    .single-fs-expanded .q-text { font-size: calc(clamp(1.4rem, 3.5vw, 4.5rem) * var(--text-scale-factor)) !important; }
                    .single-fs-expanded .back .q-text { font-size: calc(clamp(1.1rem, 2.2vw, 2.8rem) * var(--text-scale-factor)) !important; }
                    .single-fs-expanded .a-text { font-size: calc(clamp(1.8rem, 4.5vw, 6rem) * var(--text-scale-factor)) !important; }
                `}</style>

                {/* ── HEADER (both modes) ── */}
                <div className={`${isFullscreen ? "fs-header" : "flex items-center gap-3 px-6 py-4 border-b bg-card/80 flex-shrink-0"}`}>
                    {!isFullscreen && (
                        <Button variant="ghost" size="icon" onClick={() => { clearSingleFullscreenCloseTimer(); clearSingleFullscreenOpenFrame(); setClosingFullscreenCardIdx(null); setFullscreenCardIdx(null); setFullscreenCardRect(null); setIsFullscreenCardExpanded(false); setIsGenerated(false); }}>
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                    )}
                    {!isFullscreen && (
                        <div className="flex items-center gap-2 flex-1">
                            <Sparkles className="h-5 w-5 text-primary" />
                            <span className="text-xl font-bold font-serif">Classroom Starters</span>
                            <span className="text-sm text-muted-foreground ml-2">{generatedQuestions.length} questions</span>
                        </div>
                    )}
                    {isFullscreen && <span className="text-sm font-semibold mr-2 text-muted-foreground">Classroom Starters</span>}

                    <div className="flex gap-2 flex-wrap ml-auto">
                        <Button
                            size="sm" variant="outline"
                            className={isFullscreen ? "ctrl" : ""}
                            onClick={() => setTextSizeMultiplier(m => Math.max(0.4, m - 0.2))}
                        >
                            A-
                        </Button>
                        <Button
                            size="sm" variant="outline"
                            className={isFullscreen ? "ctrl" : ""}
                            onClick={() => setTextSizeMultiplier(m => Math.min(2.5, m + 0.2))}
                        >
                            A+
                        </Button>
                        <Button
                            size="sm" variant="outline"
                            className={isFullscreen ? "ctrl" : ""}
                            onClick={() => setRevealedAnswers(new Set())}
                        >
                            <EyeOff className="h-3 w-3 mr-1" />Hide All
                        </Button>
                        <Button
                            size="sm" variant="outline"
                            className={isFullscreen ? "ctrl" : ""}
                            onClick={() => setRevealedAnswers(new Set(generatedQuestions.map((_, i) => i)))}
                        >
                            <Eye className="h-3 w-3 mr-1" />Reveal All
                        </Button>
                        <Button
                            size="sm" variant="outline"
                            className={isFullscreen ? "ctrl" : ""}
                            onClick={() => { clearSingleFullscreenCloseTimer(); clearSingleFullscreenOpenFrame(); setClosingFullscreenCardIdx(null); setFullscreenCardIdx(null); setFullscreenCardRect(null); setIsFullscreenCardExpanded(false); setIsGenerated(false); if (isFullscreen) exitFullscreen(); }}
                        >
                            <RefreshCw className="h-3 w-3 mr-1" />New Set
                        </Button>
                        <Button
                            size="sm" variant={isDrawingMode ? "default" : "outline"}
                            className={`${isFullscreen ? "ctrl" : ""} ${isDrawingMode ? "bg-primary text-primary-foreground" : ""}`}
                            onClick={() => setIsDrawingMode(!isDrawingMode)}
                            title="Toggle Draw Mode"
                        >
                            <PenTool className="h-4 w-4 mr-1" />Draw
                        </Button>
                        <Button
                            size="sm" variant={isFullscreen ? "secondary" : "default"}
                            className={isFullscreen ? "ctrl" : ""}
                            onClick={isFullscreen ? exitFullscreen : enterFullscreen}
                        >
                            {isFullscreen
                                ? <><Minimize className="h-3 w-3 mr-1" />Exit</>
                                : <><Maximize className="h-3 w-3 mr-1" />Fullscreen</>
                            }
                        </Button>
                    </div>
                </div>

                {/* Floating Drawing Controls for Main Board */}
                {isDrawingMode && (
                    <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[60] bg-background/95 backdrop-blur shadow-lg border rounded-full px-4 py-2 flex items-center gap-3">
                        <div className="flex gap-1 mr-2 border-r pr-3">
                            {colors.map(c => (
                                <button
                                    key={c}
                                    className="w-6 h-6 rounded-full border-2 border-transparent focus:outline-none transition-transform hover:scale-110"
                                    style={{ backgroundColor: c }}
                                    onClick={() => {
                                        getActiveMainCanvasRef().current?.setStrokeWidth(brushSize[0]);
                                        getActiveMainCanvasRef().current?.setColor(c);
                                    }}
                                />
                            ))}
                        </div>
                        <div className="w-24 mr-2 border-r pr-3 flex items-center">
                            <Slider 
                                value={brushSize} 
                                onValueChange={(v) => {
                                    setBrushSize(v);
                                    getActiveMainCanvasRef().current?.setStrokeWidth(v[0]);
                                }} 
                                max={20} min={1} step={1} 
                            />
                        </div>
                        <Button variant={mainEraserType === 'brush' ? 'secondary' : 'ghost'} size="icon" className={`h-8 w-8 rounded-full ${mainEraserType === 'brush' ? 'ring-2 ring-primary/50' : ''}`} onClick={() => { setMainEraserType('brush'); getActiveMainCanvasRef().current?.setEraserMode(true, 'brush'); getActiveMainCanvasRef().current?.setStrokeWidth(brushSize[0] * 3); }} title="Brush Eraser">
                            <Eraser className="h-4 w-4" />
                        </Button>
                        <Button variant={mainEraserType === 'stroke' ? 'secondary' : 'ghost'} size="icon" className={`h-8 w-8 rounded-full ${mainEraserType === 'stroke' ? 'ring-2 ring-primary/50' : ''}`} onClick={() => { setMainEraserType('stroke'); getActiveMainCanvasRef().current?.setEraserMode(true, 'stroke'); }} title="Stroke Eraser">
                            <MousePointerClick className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={() => getActiveMainCanvasRef().current?.undo()} title="Undo">
                            <Undo className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-red-500 hover:text-red-600 hover:bg-red-100" onClick={() => getActiveMainCanvasRef().current?.clear()} title="Clear All">
                            <Trash2 className="h-4 w-4" />
                        </Button>
                        <div className="border-l pl-3 ml-1">
                            <Button variant="destructive" size="sm" className="h-7 px-3 rounded-full text-xs font-semibold" onClick={() => setIsDrawingMode(false)} title="Exit Draw Mode">
                                Done
                            </Button>
                        </div>
                    </div>
                )}

                {/* ── CARD GRID ── */}
                <div className={`relative ${isFullscreen ? "fs-grid" : "windowed-grid px-6 py-6"}`}>
                    {/* Drawing canvas overlay - covers the grid area only (not the header) */}
                    {isFullscreen
                        ? <DrawingCanvas ref={fsCanvasRef} isDrawingMode={isDrawingMode} />
                        : <DrawingCanvas ref={mainCanvasRef} isDrawingMode={isDrawingMode} />
                    }
                    {generatedQuestions.map((q, idx) => {
                        const isFlipped = revealedAnswers.has(idx);
                        const diff = cardDifficulties.get(idx) ?? q.difficulty;
                        const isSingleFs = fullscreenCardIdx === idx;
                        const isSingleFsClosing = closingFullscreenCardIdx === idx;
                        const isSingleFsActive = isSingleFs || isSingleFsClosing;
                        const singleFsStyle: React.CSSProperties | undefined = isSingleFsActive && fullscreenCardRect ? {
                            position: "fixed",
                            top: isFullscreenCardExpanded && !isSingleFsClosing ? 24 : fullscreenCardRect.top,
                            left: isFullscreenCardExpanded && !isSingleFsClosing ? 24 : fullscreenCardRect.left,
                            width: isFullscreenCardExpanded && !isSingleFsClosing ? "calc(100vw - 48px)" : fullscreenCardRect.width,
                            height: isFullscreenCardExpanded && !isSingleFsClosing ? "calc(100vh - 48px)" : fullscreenCardRect.height,
                            margin: 0,
                            zIndex: 9999,
                            background: "hsl(var(--background) / 0.98)",
                            borderRadius: 24,
                            boxShadow: isFullscreenCardExpanded && !isSingleFsClosing ? "0 28px 80px rgb(0 0 0 / 0.32)" : "0 18px 50px rgb(0 0 0 / 0.18)",
                            overflow: "hidden",
                        } : undefined;
                        return (
                            <div
                                key={idx}
                                className={`${isFullscreen ? "flip-scene" : "windowed-flip-scene"} ${isSingleFsActive ? "single-fs-host" : ""}`}
                            >

                                <div
                                    ref={(element) => {
                                        singleCardRefs.current[idx] = element;
                                    }}
                                    className={`${isSingleFsActive ? "single-fs" : "relative w-full h-full"} ${isSingleFsActive && !isFullscreenCardExpanded ? "single-fs-expanding" : ""} ${isSingleFsClosing ? "single-fs-closing" : ""} ${isSingleFsActive && isFullscreenCardExpanded && !isSingleFsClosing ? "single-fs-expanded" : ""} ${isSingleFsActive && isSingleCardDrawingMode ? "single-drawing-active" : ""}`}
                                    style={singleFsStyle}
                                >
                                    {isSingleFsActive && (
                                        <>
                                            <DrawingCanvas ref={singleCanvasRef} isDrawingMode={isSingleCardDrawingMode} defaultStrokeWidth={5} className="z-[60]" />
                                            {isSingleCardDrawingMode && (
                                                <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[100] bg-background/95 backdrop-blur shadow-lg border rounded-full px-4 py-2 flex items-center gap-3">
                                                    <div className="flex gap-1 mr-2 border-r pr-3">
                                                        {colors.map(c => (
                                                            <button
                                                                key={c}
                                                                className="w-8 h-8 rounded-full border-2 border-transparent focus:outline-none transition-transform hover:scale-110 shadow-sm"
                                                                style={{ backgroundColor: c }}
                                                                onClick={() => {
                                                                    singleCanvasRef.current?.setStrokeWidth(singleBrushSize[0]);
                                                                    singleCanvasRef.current?.setColor(c);
                                                                }}
                                                            />
                                                        ))}
                                                    </div>
                                                    <div className="w-32 mr-2 border-r pr-3 flex items-center">
                                                        <Slider 
                                                            value={singleBrushSize} 
                                                            onValueChange={(v) => {
                                                                setSingleBrushSize(v);
                                                                singleCanvasRef.current?.setStrokeWidth(v[0]);
                                                            }} 
                                                            max={30} min={1} step={1} 
                                                        />
                                                    </div>
                                                    <Button variant={singleEraserType === 'brush' ? 'secondary' : 'ghost'} size="icon" className={`h-10 w-10 rounded-full ${singleEraserType === 'brush' ? 'ring-2 ring-primary/50' : ''}`} onClick={() => { setSingleEraserType('brush'); singleCanvasRef.current?.setEraserMode(true, 'brush'); singleCanvasRef.current?.setStrokeWidth(singleBrushSize[0] * 3); }} title="Brush Eraser">
                                                        <Eraser className="h-5 w-5" />
                                                    </Button>
                                                    <Button variant={singleEraserType === 'stroke' ? 'secondary' : 'ghost'} size="icon" className={`h-10 w-10 rounded-full ${singleEraserType === 'stroke' ? 'ring-2 ring-primary/50' : ''}`} onClick={() => { setSingleEraserType('stroke'); singleCanvasRef.current?.setEraserMode(true, 'stroke'); }} title="Stroke Eraser">
                                                        <MousePointerClick className="h-5 w-5" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full" onClick={() => singleCanvasRef.current?.undo()} title="Undo">
                                                        <Undo className="h-5 w-5" />
                                                    </Button>
                                                    <Button variant="ghost" size="icon" className="h-10 w-10 rounded-full text-red-500" onClick={() => singleCanvasRef.current?.clear()} title="Clear All">
                                                        <Trash2 className="h-5 w-5" />
                                                    </Button>
                                                    <div className="border-l pl-3 ml-1">
                                                        <Button variant="destructive" size="sm" className="h-8 px-3 rounded-full text-xs font-semibold" onClick={() => setIsSingleCardDrawingMode(false)} title="Exit Draw Mode">
                                                            Done
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    <div className={`flip-inner ${isFlipped ? "flipped" : ""}`}>

                                        {/* ── FRONT: Question ── */}
                                        <div className="flip-face front">
                                            <div className="card-header">
                                                <div className="flex items-center gap-2">
                                                    <div className="q-badge">{idx + 1}</div>
                                                    <div className="flex flex-col gap-0.5">
                                                        <Select value={cardTopics.get(idx) || q.topicName} onValueChange={v => changeCardTopic(idx, v)}>
                                                            <SelectTrigger className="h-6 text-xs border-0 bg-transparent shadow-none p-0 min-w-[110px]">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent container={isFullscreen ? containerRef.current : undefined}>
                                                                {topics.map(t => (
                                                                    <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                        <Select value={String(diff)} onValueChange={v => updateDifficulty(idx, Number(v) as DifficultyLevel)}>
                                                            <SelectTrigger className="h-5 text-[10px] border-0 bg-transparent shadow-none p-0 min-w-[80px]">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent container={isFullscreen ? containerRef.current : undefined}>
                                                                {[4, 5, 6, 7, 8, 9].map(n => <SelectItem key={n} value={String(n)}>Grade {n}</SelectItem>)}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => refreshCard(idx)} title="New question">
                                                        <RefreshCw className="h-3 w-3" />
                                                    </Button>
                                                    {isSingleFsActive && (
                                                        <>
                                                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setTextSizeMultiplier(m => Math.max(0.4, m - 0.2))} title="Decrease text size">
                                                                A-
                                                            </Button>
                                                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setTextSizeMultiplier(m => Math.min(2.5, m + 0.2))} title="Increase text size">
                                                                A+
                                                            </Button>
                                                        </>
                                                    )}
                                                    {isSingleFsActive && (
                                                        <Button size="icon" variant={isSingleCardDrawingMode ? "default" : "ghost"} className={`h-7 w-7 ${isSingleCardDrawingMode ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}`} onClick={() => setIsSingleCardDrawingMode(!isSingleCardDrawingMode)} title="Toggle Draw Mode">
                                                            <PenTool className="h-3 w-3" />
                                                        </Button>
                                                    )}
                                                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toggleSingleFullscreen(idx)} title="Fullscreen">
                                                        {isSingleFsActive ? <Minimize className="h-3 w-3" /> : <Maximize className="h-3 w-3" />}
                                                    </Button>
                                                    <Button size="sm" variant="default" className="h-7 px-2 sm:px-3 whitespace-nowrap" onClick={() => toggleReveal(idx)} title="Show Answer">
                                                        <Eye className="h-3 w-3 sm:mr-1" />
                                                        <span className="hidden sm:inline text-xs">Answer</span>
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="card-body">
                                                <div className="q-text math-serif">
                                                    <MathDisplay>{q.question}</MathDisplay>
                                                </div>
                                            </div>
                                        </div>

                                        {/* ── BACK: Answer ── */}
                                        <div className="flip-face back">
                                            <div className="card-header">
                                                <div className="flex items-center gap-2">
                                                    <div className="q-badge" style={{ background: 'hsl(142 60% 35%)', color: 'white' }}>{idx + 1}</div>
                                                    <span className="text-xs font-semibold text-muted-foreground mr-2 truncate max-w-[100px] sm:max-w-none">{cardTopics.get(idx) || q.topicName}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {isSingleFsActive && (
                                                        <>
                                                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setTextSizeMultiplier(m => Math.max(0.4, m - 0.2))} title="Decrease text size">
                                                                A-
                                                            </Button>
                                                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setTextSizeMultiplier(m => Math.min(2.5, m + 0.2))} title="Increase text size">
                                                                A+
                                                            </Button>
                                                        </>
                                                    )}
                                                    {isSingleFsActive && (
                                                        <Button size="icon" variant={isSingleCardDrawingMode ? "default" : "ghost"} className={`h-7 w-7 ${isSingleCardDrawingMode ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}`} onClick={() => setIsSingleCardDrawingMode(!isSingleCardDrawingMode)} title="Toggle Draw Mode">
                                                            <PenTool className="h-3 w-3" />
                                                        </Button>
                                                    )}
                                                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toggleSingleFullscreen(idx)} title="Fullscreen">
                                                        {isSingleFsActive ? <Minimize className="h-3 w-3" /> : <Maximize className="h-3 w-3" />}
                                                    </Button>
                                                    <Button size="sm" variant="outline" className="h-7 px-2 sm:px-3 whitespace-nowrap" onClick={() => toggleReveal(idx)} title="Show Question">
                                                        <EyeOff className="h-3 w-3 sm:mr-1" />
                                                        <span className="hidden sm:inline text-xs">Question</span>
                                                    </Button>
                                                </div>
                                            </div>
                                            <div className="card-body">
                                                <div className="q-text math-serif">
                                                    <MathDisplay>{q.question}</MathDisplay>
                                                </div>
                                                <div className="w-full border-t-2 border-green-300/60 pt-3 mt-1">
                                                    <p className="text-xs font-bold uppercase tracking-widest text-green-700 dark:text-green-400 mb-2">Answer</p>
                                                    <div className="a-text math-serif">
                                                        <MathDisplay>{q.answer}</MathDisplay>
                                                    </div>
                                                </div>
                                                {!isFullscreen && q.explanation && (
                                                    <p className="text-sm text-muted-foreground mt-3 max-w-md leading-relaxed">
                                                        <MathText>{q.explanation}</MathText>
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

            </div>
        );
    }

    // ── SETUP SCREEN ─────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-background">
            <div className="container mx-auto px-4 py-8 max-w-4xl">
                <div className="space-y-8">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="icon" onClick={() => setLocation("/teacher-dashboard")}>
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-primary/10 rounded-lg">
                                <Sparkles className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold font-serif">Classroom Starters</h1>
                                <p className="text-muted-foreground mt-1">
                                    Select between 4, 6, or 8 questions for your fullscreen starter. Select up to {starterCount} topics.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="flex flex-col gap-4 mb-8 p-6 bg-card border rounded-lg shadow-sm">
                        <div className="flex items-center justify-between pointer-events-none">
                            <h3 className="text-lg font-semibold flex items-center gap-2">
                                <ListOrdered className="h-5 w-5 text-primary" />
                                How many questions?
                            </h3>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            {[4, 6, 8].map(num => (
                                <Button
                                    key={num}
                                    variant={starterCount === num ? "default" : "outline"}
                                    onClick={() => {
                                        setStarterCount(num);
                                        if (selectedTopics.length > num) {
                                            setSelectedTopics(selectedTopics.slice(0, num));
                                        }
                                    }}
                                    className="px-6 relative shadow-sm"
                                >
                                    <span className="text-lg">{num} Questions</span>
                                </Button>
                            ))}
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                            A grid of {starterCount} question cards will be generated. You can pick up to {starterCount} different topics below. 
                        </p>
                        {isTeacher && (
                            <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                                <Switch
                                    id="show-only-my-topics"
                                    checked={showOnlyMyTopics}
                                    onCheckedChange={setShowOnlyMyTopics}
                                />
                                <Label htmlFor="show-only-my-topics" className="cursor-pointer">
                                    Show only my custom uploaded questions
                                </Label>
                            </div>
                        )}
                    </div>

                    <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
                        <h3 className="text-lg font-semibold">
                            Select Topics ({selectedTopics.length}/{starterCount} selected)
                        </h3>
                        <div className="flex items-center gap-2 flex-1 md:w-auto min-w-[200px] md:max-w-md ml-auto">
                            <div className="relative w-full">
                                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                <Input
                                    type="text"
                                    placeholder="Search topics..."
                                    className="pl-9 w-full"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                        {loading ? (
                            <div className="flex justify-center p-8">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            </div>
                        ) : (
                            <Accordion type="multiple" className="w-full space-y-4">
                                {CATEGORY_ORDER.filter(cat => visibleTopics.some(t => getYearCategory(t.yearLevel) === cat)).map(category => {
                                    // Get all topics for this Key Stage category
                                    const topicsInCategory = visibleTopics.filter(t => getYearCategory(t.yearLevel) === category);
                                    if (topicsInCategory.length === 0) return null;
                                    
                                    // Group them by Year Level, then by sub-category
                                    const yearGroups = topicsInCategory.reduce((acc: Record<string, Record<string, Topic[]>>, t: Topic) => {
                                        const yearStr = formatYearGroup(t.yearLevel);
                                        if (!acc[yearStr]) acc[yearStr] = {};
                                        const cat = t.category || "Uncategorized";
                                        if (!acc[yearStr][cat]) acc[yearStr][cat] = [];
                                        acc[yearStr][cat].push(t);
                                        return acc;
                                    }, {});

                                    return (
                                        <AccordionItem key={category} value={category} className="border bg-card rounded-lg px-4 shadow-sm">
                                            <AccordionTrigger className="hover:no-underline font-serif text-xl py-4">
                                                {category}
                                            </AccordionTrigger>
                                            <AccordionContent>
                                                <div className="space-y-8 pt-2 pb-4">
                                                    {Object.entries(yearGroups)
                                                        .sort(([yearA], [yearB]) => yearA.localeCompare(yearB, undefined, {numeric: true}))
                                                        .map(([yearStr, subGroups]) => (
                                                        <div key={yearStr} className="space-y-4">
                                                            <h3 className="text-lg font-bold border-b pb-2">{yearStr}</h3>
                                                            <div className="space-y-6">
                                                                {Object.entries(subGroups)
                                                                    .sort(([catA], [catB]) => catA.localeCompare(catB))
                                                                    .map(([cat, topics]) => (
                                                                    <div key={cat}>
                                                                        <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                                                                            {cat}
                                                                        </h4>
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                                                            {topics.map(topic => (
                                                                                <div
                                                                                    key={topic.id}
                                                                                    className={`p-3 rounded-lg border-2 cursor-pointer transition-all flex items-center gap-3 ${selectedTopics.includes(topic.id)
                                                                                        ? "border-primary bg-primary/5 shadow-sm"
                                                                                        : "border-border hover:border-primary/30 hover:bg-muted/50"
                                                                                        } ${selectedTopics.length >= starterCount && !selectedTopics.includes(topic.id) ? "opacity-50 pointer-events-none" : ""}`}
                                                                                    onClick={() => toggleTopic(topic.id)}
                                                                                >
                                                                                    <Checkbox
                                                                                        checked={selectedTopics.includes(topic.id)}
                                                                                        className="pointer-events-none"
                                                                                    />
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <p className="font-medium truncate" title={topic.name}>{topic.name}</p>
                                                                                        <p className="text-xs text-muted-foreground">{topic.questions.length} questions</p>
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </AccordionContent>
                                        </AccordionItem>
                                    );
                                })}
                            </Accordion>
                        )}
                    </div>

                    {/* Spacer for floating bar */}
                    <div className="h-24" />
                </div>
            </div>

            {/* ── Floating Action Bar ── */}
            <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center pb-5 px-4 pointer-events-none">
                <div className="pointer-events-auto flex items-center gap-3 px-6 py-3 rounded-full border bg-background/80 backdrop-blur-xl shadow-[0_-4px_30px_rgb(0_0_0/0.12)] dark:shadow-[0_-4px_30px_rgb(0_0_0/0.35)] transition-all">
                    {selectedTopics.length > 0 && (
                        <>
                            <span className="text-sm text-muted-foreground font-medium">
                                {selectedTopics.length}/{starterCount} selected
                            </span>
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => setSelectedTopics([])}
                                className="text-muted-foreground whitespace-nowrap h-9"
                            >
                                Deselect All
                            </Button>
                            <div className="w-px h-6 bg-border" />
                        </>
                    )}
                    <Button
                        size="lg"
                        onClick={handleGenerate}
                        disabled={selectedTopics.length === 0}
                        className="gap-2 rounded-full px-8 shadow-md"
                    >
                        <Sparkles className="h-4 w-4" />
                        Generate Starters
                    </Button>
                </div>
            </div>
        </div>
    );
}

